import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {SideReelFeature} from "./features/SideReelFeature";
import {gsap} from "gsap";
import {FeatureList, GameConfig, SideReelFeaturesList} from "../../../GameConfig";
import {ExtendedGameModel} from "../../../server/ExtendedGameModel";
import {HOTB} from "../../../HOTB";
import {GameUtils} from "../../../utils/GameUtils";
import {AnimUtils} from "../../../utils/AnimUtils";
import {XSplitFeature} from "./features/XSplitFeature";
import {MultiplierFeature} from "./features/MultiplierFeature";
import {XBombFeature} from "./features/XBombFeature";
import {BlankFeature} from "./features/BlankFeature";
import {GameSymbol, SymbolsList} from "../../../reels/GameSymbol";
import {HOTBData} from "../../../server/HOTBData";
import {ISymbol} from "../../../../core/reel/IReelData";
import {GameSounds} from "../../../sound/GameSounds";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";

export class SideReelsView extends PIXI.Container implements StageLayer {
    private _maskArea: PIXI.Graphics;
    private _currentReelFeatures: SideReelFeature[] | undefined[] = [];
    private _smokeDust: TimelineSprite;
    private _rays: PIXI.spine.Spine;

    constructor(name: string) {
        super();
        this.name = name;
        this.init();
    }

    private _isRaysActive: boolean = false;

    get isRaysActive(): boolean {
        return this._isRaysActive;
    }

    set isRaysActive(value: boolean) {
        this._isRaysActive = value;
    }

    public show(): void {
        this.visible = true;
    }

    public hide(): void {
        this.visible = false;
    }

    public onSpinStart(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = this._currentReelFeatures.length - 1; i >= 0; i--) {
            const currFeature: SideReelFeature | undefined = this._currentReelFeatures[i];

            if (currFeature) {
                tl.add(currFeature.pushOut(), GameConfig.pushDelayBetweenItems * (this._currentReelFeatures.length - i));
            }
        }

        return tl;
    }

    public onGameLoad(): void {
        this.placeFeatures(0.001);
    }

    public onGameResult(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this.placeFeatures(GameConfig.pushDelayBetweenItems));

        return tl;
    }

    public activateReel(activeFeatureDelay: number[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this.triggerMultipliers());
        tl.add(this.triggerSplit(), (activeFeatureDelay[0] ? activeFeatureDelay[0] : tl.totalDuration()));
        tl.add(this.triggerBombs(), ((activeFeatureDelay[0] + activeFeatureDelay[1]) ? (activeFeatureDelay[0] + activeFeatureDelay[1]) : tl.totalDuration()));

        tl.add(this.stopRaysAnimation());
        tl.add((): void => {
            if (this._currentReelFeatures.length > 0 && !HOTB.soundPlayer.isSideReelSoundPlaying) {
                HOTB.soundPlayer.playSound(GameSounds.PLACE_FEATURES_SIDE_REEL);
                HOTB.soundPlayer.isSideReelSoundPlaying = true;
            }
            this.placeFeatures(0.001, true);
        });

        return tl;
    }

    public startRaysAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            leftReelData: string[] = gameModel.data.reelLeft,
            rightReelData: string[] = gameModel.data.reelRight,
            currReelData: string[] = this.isLeftReel() ? leftReelData : rightReelData,
            isCurrReelBlank: boolean = GameUtils.hasSameValues(currReelData, FeatureList.BLANK);

        if (isCurrReelBlank || this.isRaysActive) {
            return tl;
        }

        this.isRaysActive = true;

        const raysStartAnimName: string = "1_raysStart",
            raysLoopAnimName: string = "2_raysLoop";

        if (this.isLeftReel() || (!(this.isLeftReel()) && GameUtils.hasSameValues(leftReelData, FeatureList.BLANK))) {
            HOTB.soundPlayer.playSound(GameSounds.RAYS_START);
        }

        tl.add(AnimUtils.playSpineAnimation({
            spineObj: this._rays,
            animation: raysStartAnimName,
        }));

        tl.add([
            (): void => {
                //this.isRaysActive = true;
                this._currentReelFeatures.forEach((currFeature: SideReelFeature | undefined, index: number): void => {
                    if (currFeature) {
                        currFeature.toggleTint(false);
                    }
                });
            },
            AnimUtils.playSpineAnimation({
                spineObj: this._rays,
                animation: raysLoopAnimName,
                playLoop: true
            })
        ]);

        return tl;
    }

    public reInit(): void {
        this.removeChildren();
        this.init();
        this.placeFeatures(0.0001, true);
    }

    public checkFeatureTypesPresent(): number[] {
        const hotbData: HOTBData = HOTB.model.data,
            sideReelData: string[] = this.isLeftReel() ? hotbData.reelLeft : hotbData.reelRight,
            featureTypesPresent: number[] = [0, 0],
            possibleMultipliers: Set<FeatureList> = new Set([
                FeatureList.X2,
                FeatureList.X3,
                FeatureList.X4,
                FeatureList.X5,
                FeatureList.X99
            ]);

        for (let i: number = 0; i < sideReelData.length; i++) {
            if (possibleMultipliers.has(sideReelData[i] as FeatureList)) {
                featureTypesPresent[0] += 1;
            } else if ((sideReelData[i] as FeatureList) === FeatureList.XSPLIT) {
                featureTypesPresent[1] += 1;
            }
        }

        return featureTypesPresent;
    }

    private stopRaysAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            raysStopAnim: string = "3_raysStop",
            raysStopDur: number = AnimUtils.getSpineDuration(this._rays, raysStopAnim);

        if (!(this.isRaysActive)) {
            return tl;
        }

        tl.add(AnimUtils.playSpineAnimation({
            spineObj: this._rays,
            animation: raysStopAnim
        }));

        tl.add((): void => {
            this.isRaysActive = false;
        });

        tl.add(this.placeFeatures(0.01), raysStopDur);

        return tl;
    }

    private triggerMultipliers(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            hotbData: HOTBData = HOTB.model.data,
            sideReelData: string[] = this.isLeftReel() ? hotbData.reelLeft : hotbData.reelRight,
            possibleMultipliers: Set<FeatureList> = new Set([
                FeatureList.X2,
                FeatureList.X3,
                FeatureList.X4,
                FeatureList.X5,
                FeatureList.X99
            ]);

        // Return if no possible multipliers are found
        if (!sideReelData.some(item => possibleMultipliers.has(item as FeatureList))) {
            return tl;
        }

        // Add the launch multiplier action to the timeline
        const launchBtnPos: [number, number] | null = this.getLaunchPos();
        if (launchBtnPos) {
            tl.add((): void => {
                const launchSymbol: ISymbol = HOTB.reelArea.getSymbolAtPosition(launchBtnPos[0], launchBtnPos[1]);
                (<GameSymbol>launchSymbol).showLaunchMulti();
            }, 0);
        }

        // Add multipliers to the timeline
        this._currentReelFeatures.forEach(currFeature => {
            if (currFeature && possibleMultipliers.has(currFeature.featureName as FeatureList)) {

                tl.add((currFeature as MultiplierFeature).fireAmmo(), 0.3333);

                tl.add((): void => {
                    this.removeFeature(currFeature);
                }, 0.3333 + (currFeature as MultiplierFeature).getFireAmmoDuration());
            }
        });

        return tl;
    }

    private triggerSplit(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            hotbData: HOTBData = HOTB.model.data,
            sideReelData: string[] = this.isLeftReel() ? hotbData.reelLeft : hotbData.reelRight;

        // Return if no possible xSplit are found
        if (!sideReelData.includes(SideReelFeaturesList.XSPLIT)) {
            return tl;
        }

        let firstXSplitSoundHandled: boolean = false;

        // Add xSplit to the timeline
        for (const currFeature of this._currentReelFeatures) {
            if (currFeature?.featureName === FeatureList.XSPLIT) {

                tl.add((currFeature as XSplitFeature).startSplit(!firstXSplitSoundHandled), 0);

                if (!firstXSplitSoundHandled) {
                    firstXSplitSoundHandled = true;
                }

                tl.add((): void => {
                    this.removeFeature(currFeature);
                });
            }
        }

        return tl;
    }

    private triggerBombs(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            hotbData: HOTBData = HOTB.model.data,
            sideReelData: string[] = this.isLeftReel() ? hotbData.reelLeft : hotbData.reelRight;

        // Return if no possible xBomb are found
        if (!sideReelData.includes(SideReelFeaturesList.XBOMB)) {
            return tl;
        }

        // Add xBomb to the timeline
        for (const currFeature of this._currentReelFeatures) {
            if (currFeature?.featureName === FeatureList.XBOMB) {
                tl.add((currFeature as XBombFeature).triggerBomb(), 0);
                tl.add((): void => {
                    this.removeFeature(currFeature);
                });
            }
        }

        return tl;
    }

    private getLaunchPos(): [number, number] | null {
        const symbolsData: string[][] = HOTB.model.gameData.symbolsAfterAvalanchePrevSpin;

        for (let reelId: number = 0; reelId < symbolsData.length; reelId++) {
            const reel: string[] = symbolsData[reelId];

            for (let symId: number = 0; symId < reel.length; symId++) {
                if (reel[symId] === SymbolsList.LAUNCH) {
                    return [reelId, symId];
                }
            }
        }
        return null;
    }

    private init(): void {
        this.position.set(0, 0);
        this.createMask();
        this.createSmokeSeq();
        this.createRays();
    }

    private createMask(): void {
        this._maskArea = new PIXI.Graphics();
        this._maskArea.beginFill(0xFFFF00, 0.5);
        this._maskArea.drawRoundedRect(0, 200, 110, 503, 45);
        this._maskArea.endFill();
        this._maskArea.beginFill(0xFFFF00, 0.5);
        this._maskArea.drawRoundedRect(610, 200, 110, 503, 45);
        this._maskArea.endFill();
        this._maskArea.beginFill(0xFFFF00, 0.5);
        this._maskArea.drawShape(new PIXI.Rectangle(0, 10, 730, 675));
        this._maskArea.position.set(0, 0);
        this._maskArea.endFill();
        this.mask = this._maskArea;
        this.addChild(this._maskArea);
    }

    private isLeftReel(): boolean {
        return this.name.indexOf("Left") > -1;
    }

    private createSmokeSeq(): void {
        this._smokeDust = AnimUtils.createImageSeq("sideReelSmoke");
        this._smokeDust.blendMode = PIXI.BLEND_MODES.ADD;
        this._smokeDust.position.set(this.isLeftReel() ? 50 : 660, 450);
        this._smokeDust.playLoop();
        this._smokeDust.show();
        this.addChild(this._smokeDust);
    }

    private createRays(): void {
        this.isRaysActive = false;
        this._rays = AnimUtils.createNewSpine({
            path: "sideReelRays",
            name: this.isLeftReel() ? "leftRays" : "rightRays",
            pos: new PIXI.Point(this.isLeftReel() ? 50 : 660, 450),
            initShow: true
        });

        this.addChild(this._rays);
    }

    private placeFeatures(delay: number, isForced: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            reelData: string[] = this.isLeftReel() ? gameModel.data.reelLeft : gameModel.data.reelRight,
            reelDataNew: string[] = this.isLeftReel() ? gameModel.data.reelLeftNew : gameModel.data.reelRightNew;


        if (!gameModel.isRestore) {
            // return if in "AVALANCHE" mode and not forced
            if (gameModel.data.mode.includes("AVALANCHE") && !isForced) {
                return tl;
            }
        }

        this._currentReelFeatures = [];

        const reelDataToShowItems: string[] = reelDataNew || reelData;

        reelDataToShowItems.forEach((featureName: string, rowId: number): void => {
            const sideStr: string = this.isLeftReel() ? "left" : "right";

            // Skip if feature name is blank or already set
            if (featureName === FeatureList.BLANK || this._currentReelFeatures[rowId]?.featureName === featureName) {
                return;
            }

            let featureItem: SideReelFeature;

            // Assign appropriate feature based on the feature name
            switch (featureName) {
                case FeatureList.XSPLIT:
                    featureItem = new XSplitFeature(rowId, sideStr, featureName);
                    break;
                case FeatureList.X2:
                case FeatureList.X3:
                case FeatureList.X4:
                case FeatureList.X5:
                case FeatureList.X99:
                    featureItem = new MultiplierFeature(rowId, sideStr, featureName);
                    break;
                case FeatureList.XBOMB:
                    featureItem = new XBombFeature(rowId, sideStr, featureName);
                    break;
                default:
                    featureItem = new BlankFeature(rowId, sideStr, featureName);
                    break;
            }

            this._currentReelFeatures[rowId] = featureItem;
            this.addChild(featureItem);
        });

        // Add feature animations to the timeline
        this._currentReelFeatures.forEach((currFeature: SideReelFeature | undefined, index: number): void => {
            if (currFeature) {
                tl.add(currFeature.pushIn(), delay * index);
            }
        });

        if (gameModel.isActionSpinToBonus) {
            tl.totalProgress(1);
        }

        return tl;
    }

    private removeFeature(feature: SideReelFeature): void {
        const featureAtRow: SideReelFeature | undefined = this._currentReelFeatures[feature.rowId];

        if (featureAtRow === feature) {
            this._currentReelFeatures[feature.rowId] = undefined;
        } else {
            debugger;
            throw new Error(`Feature at row ${feature.rowId} does not match the expected feature.`);
        }

        this.removeChild(feature);
    }
}